import { setNotification } from "features/Notification/notificationSlice";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import GenerateAttendance from "../../AttReports/GenerateAttendance";
import Spinner from "components/Spinner";
import { getDayNameByDate } from "utils/attendanceUtility";
import {
  getNightUserTotalHours,
  getNightUserTotalLateTime,
  getNightUserTotalOvertime,
} from "utils/NightUserAttendanceUtility";

export default function NightUpdateMonthlyRecords({
  setUserData,
  refreshFunc,
  userData,
  selectedMonth,
  setSelectedMonth,
}) {
  const dispatch = useDispatch();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  /*   All States
   ********************************************* */
  // States
  const [loading, setLoading] = useState(false);

  const [monthlyRecords, setMonthlyRecords] = useState([]);
  const [holidayList, setHolidayList] = useState([]);
  // Add state to store all original logs
  const [originalLogs, setOriginalLogs] = useState([]);

  // States from switching to next and previous user
  const [currentUserIndex, setCurrentUserIndex] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [usersList, setUsersList] = useState([]);

  const [selectedDevice, setSelectedDevice] = useState(null);

  const loadingOn = () => setLoading(true);
  const loadingOff = () => setLoading(false);

  /*   All Functions
   ********************************************* */
  // Get the number of days in the selected month
  const getDaysInMonth = useCallback(() => {
    const [year, month] = selectedMonth.split("-");
    return new Date(year, month, 0).getDate();
  }, [selectedMonth]);

  function buildNightShiftSessions(logs) {
    // Sort logs by original timestamp (UTC safe)
    logs.sort((a, b) => new Date(a.recordTime) - new Date(b.recordTime));

    // Step 1: Group logs by date and separate IN/OUT records
    const dateGroups = {};

    for (let log of logs) {
      const dt = new Date(log.recordTime);
      const hour = dt.getUTCHours();
      const rawDate = log.recordTime.slice(0, 10);

      // Initialize date group if not exists
      if (!dateGroups[rawDate]) {
        dateGroups[rawDate] = {
          inRecords: [], // hour >= 12 (IN times for this date)
          outRecords: [], // hour < 12 (OUT times, belong to PREVIOUS date)
        };
      }

      if (hour >= 12) {
        // IN record for this date
        dateGroups[rawDate].inRecords.push(log);
      } else {
        // OUT record (belongs to previous date)
        dateGroups[rawDate].outRecords.push(log);
      }
    }

    // Step 2: Build sessions by assigning IN to same date, OUT to previous date
    const sessionsMap = {}; // key: date, value: session object

    for (const [date, group] of Object.entries(dateGroups)) {
      // Process IN records (earliest IN for this date)
      if (group.inRecords.length > 0) {
        // Sort by recordTime ascending, get earliest
        group.inRecords.sort(
          (a, b) => new Date(a.recordTime) - new Date(b.recordTime),
        );
        const earliestIn = group.inRecords[0];

        if (!sessionsMap[date]) {
          sessionsMap[date] = {
            date: date,
            in: null,
            inTimeRecordId: null,
            inLog: null,
            inRecords: [],
            out: null,
            outTimeRecordId: null,
            outLog: null,
            outRecords: [],
          };
        }

        sessionsMap[date].in = earliestIn.recordTime;
        sessionsMap[date].inTimeRecordId = earliestIn.id;
        sessionsMap[date].inLog = earliestIn;
        sessionsMap[date].inRecords = group.inRecords;
      }

      // Process OUT records (latest OUT belongs to PREVIOUS date)
      if (group.outRecords.length > 0) {
        // Sort by recordTime descending, get latest
        group.outRecords.sort(
          (a, b) => new Date(b.recordTime) - new Date(a.recordTime),
        );
        const latestOut = group.outRecords[0];

        // Calculate previous date
        const prevDateObj = new Date(date + "T00:00:00Z");
        prevDateObj.setUTCDate(prevDateObj.getUTCDate() - 1);
        const prevDate = prevDateObj.toISOString().slice(0, 10);

        if (!sessionsMap[prevDate]) {
          sessionsMap[prevDate] = {
            date: prevDate,
            in: null,
            inTimeRecordId: null,
            inLog: null,
            inRecords: [],
            out: null,
            outTimeRecordId: null,
            outLog: null,
            outRecords: [],
          };
        }

        sessionsMap[prevDate].out = latestOut.recordTime;
        sessionsMap[prevDate].outTimeRecordId = latestOut.id;
        sessionsMap[prevDate].outLog = latestOut;
        sessionsMap[prevDate].outRecords = group.outRecords;
      }
    }

    // Step 3: Convert map to sorted array
    const sessions = Object.values(sessionsMap).sort(
      (a, b) => new Date(a.date) - new Date(b.date),
    );

    // console.log("logs", logs);
    // console.log("sessions", sessions);

    return sessions;
  }

  // Process logs to show only check-in and check-out for each date
  const processLogsToRecords = useCallback(
    (logs, holidayList) => {
      setOriginalLogs(logs);
      const daysInMonth = getDaysInMonth();
      const [year, month] = selectedMonth.split("-");
      const records = [];
      // Prepare a set of holiday dates for quick lookup
      const holidayDatesSet = new Set(
        holidayList.map((h) => h.date.split("T")[0]),
      );

      let recordsSessions = buildNightShiftSessions(logs);

      for (let day = 1; day <= daysInMonth; day++) {
        const dateString = `${year}-${month}-${String(day).padStart(2, "0")}`;
        let record = {
          date: dateString,
          dayLogs: [],
        };

        /**
         * REQUIRED FORMAT
         * {
            ✅"date": "2025-12-01",
            ✅"inTime": "19:00",
            ✅"outTime": "",
            ✅"inTimeRecordId": 13862,
            ✅"outTimeRecordId": null,
            ✅"calculateOverTime": true,
            ✅"calculateLateTime": true,
            ✅"dayLogs": [
                {
                    "id": 13862,
                    "companyId": 3,
                    "deviceId": 6,
                    "userId": 2,
                    "_recordTime": null,
                    "recordTime": "2025-12-01T19:00:00.000Z",
                    "isDeviceRecord": false,
                    "calculateOverTime": true,
                    "calculateLateTime": true,
                    "createdAt": "2025-12-02T17:09:55.000Z",
                    "updatedAt": "2025-12-02T17:09:55.000Z"
                }
            ],
            "isHoliday": false,
            "dayName": "Monday"
            }
         */

        let sessionForTheDay = recordsSessions.find(
          (session) => session.date === dateString,
        );
        if (!sessionForTheDay) {
          record = {
            ...record,
            inTime: "",
            outTime: "",
            inTimeRecordId: null,
            outTimeRecordId: null,
            calculateOverTime: false,
            calculateLateTime: false,
            dayLogs: [],
            isHoliday: holidayDatesSet.has(dateString),
          };
          records.push(record);
          continue;
        }

        /*   Need All Date manipulations in UTC En-US format
         **************** */

        // set inTime and outTime from session
        if (sessionForTheDay.in) {
          // record.inTime = sessionForTheDay.in;
          const inTimeDate = new Date(sessionForTheDay.in);
          const inHours = String(inTimeDate.getUTCHours()).padStart(2, "0");
          const inMinutes = String(inTimeDate.getUTCMinutes()).padStart(2, "0");

          record.inTime = `${inHours}:${inMinutes}`;
          record.inTimeRecordId = sessionForTheDay.inTimeRecordId;
          record.inLog = sessionForTheDay.inLog;
          const inRecords = sessionForTheDay.inRecords || [];
          record.inLogs = inRecords;
          record.dayLogs = [...inRecords];
        }
        if (sessionForTheDay.out) {
          record.outTime = sessionForTheDay.out;
          const outTimeDate = new Date(sessionForTheDay.out);
          const outHours = String(outTimeDate.getUTCHours()).padStart(2, "0");
          const outMinutes = String(outTimeDate.getUTCMinutes()).padStart(
            2,
            "0",
          );
          record.outTime = `${outHours}:${outMinutes}`;
          record.outTimeRecordId = sessionForTheDay.outTimeRecordId;
          record.outLog = sessionForTheDay.outLog;
          const outRecords = sessionForTheDay.outRecords || [];
          record.outLogs = outRecords;
          record.dayLogs = [...(record.dayLogs || []), ...outRecords];
        }

        // check for holiday
        record.isHoliday = holidayDatesSet.has(dateString);

        // add day name
        const dayName = new Date(dateString).toLocaleDateString("en-US", {
          weekday: "long",
          timeZone: "UTC",
        });
        record.dayName = dayName;

        // Ensure dayLogs is always an array
        const dayLogs = record.dayLogs || [];
        record.dayLogs = dayLogs;

        // Loop through logs to check if any logs for day has calculateOverTime or calculateLateTime
        record.calculateOverTime = dayLogs.some((log) => log.calculateOverTime);

        record.calculateLateTime = dayLogs.some((log) => log.calculateLateTime);

        records.push(record);
      }

      // console.log("Received Records:", logs);
      // console.log("Processed Records:", records);

      setMonthlyRecords(records);
    },
    [getDaysInMonth, selectedMonth, holidayList],
  );

  // Get logs for the selected month - wrapped in useCallback to avoid dependency issues
  const getLogsByMonth = useCallback(async () => {
    try {
      const [year, month] = selectedMonth.split("-");
      const startDate = `${year}-${month}-01`;

      // Last day of month
      const lastDay = new Date(year, month, 0).getDate();
      const endDate = `${year}-${month}-${lastDay}`;

      // Add 1 day to endDate for API call
      const endDateObj = new Date(endDate);
      endDateObj.setDate(endDateObj.getDate() + 2);
      const endDateString = endDateObj.toISOString().slice(0, 10);

      // get year from startDate
      const selectedYear = new Date(startDate).getFullYear();

      let filterBy = searchParams.get("filterBy");

      if (!filterBy) {
        filterBy = "active";
        setSearchParams({ filterBy: "active" });
      }

      loadingOn();
      const { data } = await API.post(
        "/get-user-logs/" + params.userId,
        {
          deviceId: params.deviceId,
          startDate,
          filterBy: filterBy,
          endDate: endDateString,
          year: selectedYear,
        },
        {
          headers: authHeader(),
        },
      );

      if (data?.success) {
        processLogsToRecords(data?.data, data?.holidaysList || []);
        // testNewApproach(data?.data);

        setHolidayList(data?.holidaysList || []);
        setUsersList(data?.usersList || []);
        if (data?.usersList && data?.usersList.length > 0) {
          const currentIndex = data.usersList.findIndex(
            (user) => user.userId === parseInt(params.userId, 10),
          );
          setCurrentUserIndex(currentIndex);
          setSelectedUser(data.usersList[currentIndex]);
        }
      } else {
        dispatch(
          setNotification({
            message: data?.message,
            type: "error",
          }),
        );
      }
      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  }, [
    selectedMonth,
    params.userId,
    params.deviceId,
    dispatch,
    processLogsToRecords,
    searchParams,
    setSearchParams,
  ]);

  // Navigate to the next user in the list
  const goToNextUser = useCallback(() => {
    if (usersList.length === 0 || currentUserIndex >= usersList.length - 1)
      return;

    const nextIndex = currentUserIndex + 1;
    const nextUser = usersList[nextIndex];

    setCurrentUserIndex(nextIndex);
    setSelectedUser(nextUser);

    // Update URL and fetch data for the new user

    // Update params directly for the next API call

    params.userId = nextUser.userId;

    getLogsByMonth();
  }, [usersList, currentUserIndex, params, getLogsByMonth]);

  // Navigate to the previous user in the list
  const goToPrevUser = useCallback(() => {
    if (usersList.length === 0 || currentUserIndex <= 0) return;

    const prevIndex = currentUserIndex - 1;
    const prevUser = usersList[prevIndex];

    setCurrentUserIndex(prevIndex);
    setSelectedUser(prevUser);

    // Update URL and fetch data for the new user

    // Update params directly for the next API call
    params.userId = prevUser.userId;
    getLogsByMonth();
  }, [usersList, currentUserIndex, params, getLogsByMonth]);
  // Handle input changes

  const handleRecordChange = (index, field, value, recordDate) => {
    const updatedRecords = [...monthlyRecords];
    updatedRecords[index][field] = value || "";

    // Auto-set calculateOverTime and calculateLateTime to true when changing inTime or outTime
    if (field === "inTime" || field === "outTime") {
      updatedRecords[index].calculateOverTime = true;
      updatedRecords[index].calculateLateTime = true;
    }

    setMonthlyRecords(updatedRecords);
  };

  // Handle checkbox changes: Update all records for the date
  const handleCheckBoxChange = (field, value, recordDate) => {
    // Update monthlyRecords for display
    const updatedRecords = monthlyRecords.map((record) => {
      if (record.date === recordDate) {
        // Update all logs for this day
        const updatedDayLogs = record.dayLogs.map((log) => ({
          ...log,
          [field]: value,
        }));

        return {
          ...record,
          [field]: value,
          dayLogs: updatedDayLogs,
        };
      }
      return record;
    });
    setMonthlyRecords(updatedRecords);

    // Update original logs
    const updatedOriginalLogs = originalLogs.map((log) => {
      const logDate = new Date(log.recordTime);
      const logDateString = logDate.toISOString().slice(0, 10);

      if (logDateString === recordDate) {
        return {
          ...log,
          [field]: value,
        };
      }
      return log;
    });
    setOriginalLogs(updatedOriginalLogs);
  };

  const askSaveRecordsConfirmation = () => {
    if (
      window.confirm(
        "Do you want to save " +
          selectedUser?.name +
          "'s data for the whole month for changes you have made?",
      )
    ) {
      saveRecords();
    }
  };

  // Prepare and save all records
  const saveRecords = async () => {
    try {
      // Separate records into update, create, and delete groups
      const updateData = [];
      const createData = [];
      const deleteData = [];
      // console.log("beforeSave :", monthlyRecords);

      monthlyRecords.forEach((record) => {
        const [year, month, day] = record.date.split("-");

        /*   
          Delete Logic
        **************** 
          if no dayLogs.length 
          1) and no inTimeRecordId and no outTimeRecordId
                than push all inLogs and outLogs to deleteData
          2) and if inTimeRecordId and no outTimeRecordId
                than push outLogs to deleteData
          3) and if no inTimeRecordId and outTimeRecordId
                than push inLogs to deleteData
         **************** */
        if (record.dayLogs.length) {
          // Case 1 - Both logs exist, but inTime and outTime are cleared
          if (
            (!record.inTime || record.inTime === "") &&
            (!record.outTime || record.outTime === "")
          ) {
            // Delete all logs for the day
            record?.dayLogs?.forEach((log) => {
              deleteData.push(log.id);
            });
          }
          // Case 2 - Only inTime is cleared
          else if (
            (!record.inTime || record.inTime === "") &&
            record.outTime &&
            record.outTime !== ""
          ) {
            // Delete inTime log(s)
            record?.inLogs?.forEach((log) => {
              deleteData.push(log.id);
            });
          }
          // Case 3 - Only outTime is cleared
          else if (
            record.inTime &&
            record.inTime !== "" &&
            (!record.outTime || record.outTime === "")
          ) {
            // Delete outTime log(s)
            record?.outLogs?.forEach((log) => {
              deleteData.push(log.id);
            });
          }
        }

        /* Create Logic
          **************** 
          if inTime && no inTimeRecordId
            Then push Record in Current Date
          if outTime && no outTimeRecordId
            Then push Record in Next Date
         **************** */
        if (record.inTime && !record.inTimeRecordId) {
          const [inHours, inMinutes] = record.inTime.split(":");
          const inUtcDate = new Date(
            Date.UTC(
              parseInt(year),
              parseInt(month) - 1,
              parseInt(day),
              parseInt(inHours),
              parseInt(inMinutes),
            ),
          );
          createData.push({
            recordTime: inUtcDate.toISOString(),
            deviceId: params.deviceId,
            userId: params.userId,
            calculateOverTime: record.calculateOverTime,
            calculateLateTime: record.calculateLateTime,
          });
        }
        if (record.outTime && !record.outTimeRecordId) {
          const [outHours, outMinutes] = record.outTime.split(":");
          const outUtcDate = new Date(
            Date.UTC(
              parseInt(year),
              parseInt(month) - 1,
              parseInt(day) + 1,
              parseInt(outHours),
              parseInt(outMinutes),
            ),
          );
          createData.push({
            recordTime: outUtcDate.toISOString(),
            deviceId: params.deviceId,
            userId: params.userId,
            calculateOverTime: record.calculateOverTime,
            calculateLateTime: record.calculateLateTime,
          });
        }

        /* Update Logic
          **************** 
          if inTime && inTimeRecordId
            than push that record to updateData
          if outTime && outTimeRecordId
            than push that record to updateData
         **************** */

        let previousInTime = record.inLog?.recordTime
          ?.split("T")[1]
          ?.slice(0, 5);

        // Check if time or checkbox values have changed for inTime
        const inTimeChanged = record.inTime !== previousInTime;
        const inCalculateOvertimeChanged =
          record.inLog?.calculateOverTime !== record.calculateOverTime;
        const inCalculateLateTimeChanged =
          record.inLog?.calculateLateTime !== record.calculateLateTime;

        if (
          record.inTime &&
          record.inTime !== "" &&
          record.inTimeRecordId &&
          (inTimeChanged ||
            inCalculateOvertimeChanged ||
            inCalculateLateTimeChanged)
        ) {
          const [inHours, inMinutes] = record.inTime.split(":");
          const inUtcDate = new Date(
            Date.UTC(
              parseInt(year),
              parseInt(month) - 1,
              parseInt(day),
              parseInt(inHours),
              parseInt(inMinutes),
            ),
          );
          updateData.push({
            id: record.inTimeRecordId,
            recordTime: inUtcDate.toISOString(),
            calculateOverTime: record.calculateOverTime,
            calculateLateTime: record.calculateLateTime,
          });
        }

        let previousOutTime = record.outLog?.recordTime
          ?.split("T")[1]
          ?.slice(0, 5);

        // Check if time or checkbox values have changed for outTime
        const outTimeChanged = record.outTime !== previousOutTime;
        const outCalculateOvertimeChanged =
          record.outLog?.calculateOverTime !== record.calculateOverTime;
        const outCalculateLateTimeChanged =
          record.outLog?.calculateLateTime !== record.calculateLateTime;

        if (
          record.outTime &&
          record.outTime !== "" &&
          record.outTimeRecordId &&
          (outTimeChanged ||
            outCalculateOvertimeChanged ||
            outCalculateLateTimeChanged)
        ) {
          const [outHours, outMinutes] = record.outTime.split(":");
          const outUtcDate = new Date(
            Date.UTC(
              parseInt(year),
              parseInt(month) - 1,
              parseInt(day),
              parseInt(outHours),
              parseInt(outMinutes),
            ),
          );
          updateData.push({
            id: record.outTimeRecordId,
            recordTime: outUtcDate.toISOString(),
            calculateOverTime: record.calculateOverTime,
            calculateLateTime: record.calculateLateTime,
          });
        }
      });
      // console.log("updateData ", updateData);
      // console.log("createData ", createData);
      // console.log("deleteData ", deleteData);

      loadingOn();
      const { data } = await API.post(
        "/update-monthly-logs",
        { updateData, createData, deleteData },
        { headers: authHeader() },
      );

      if (data?.success) {
        dispatch(
          setNotification({
            message: data?.message || "Records updated successfully",
            type: "success",
          }),
        );
        await getLogsByMonth();
        if (refreshFunc) refreshFunc();
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Failed to update records",
            type: "error",
          }),
        );
      }
      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  // Get Device Id
  const GetDeviceById = async (deviceId) => {
    try {
      if (!deviceId) {
        return;
      }
      loadingOn();
      const { data } = await API.get("/get-attendance-device/" + deviceId, {
        headers: authHeader(),
      });
      if (data?.success) {
        setSelectedDevice(data.data);
      }

      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    }
  };

  const getOvertime = (inTime, outTime, calculateOverTime, date, isHoliday) => {
    if (!calculateOverTime) return "0.0";
    if (!inTime || !outTime) return "0.0";
    if (isHoliday) {
      // On holidays, overtime is just total hours worked
      const totalHoursWorked = getNightUserTotalHours(inTime, outTime);
      return totalHoursWorked ? `${totalHoursWorked} hrs (H)` : "0.0";
    }

    if (!selectedUser?.weeklySchedule) {
      return "Weekly schedule not set";
    }
    let weeklySchedule = JSON.parse(selectedUser?.weeklySchedule);
    const dayName = getDayNameByDate(date);
    const isWorkingDay = weeklySchedule[dayName]?.working;
    if (!isWorkingDay) {
      const totalHoursWorked = getNightUserTotalHours(inTime, outTime);
      return totalHoursWorked; // No overtime on weekends
    }
    if (!selectedDevice) {
      return "";
    }
    let rules = JSON.parse(selectedUser?.rules || "{}");
    const dayNameOfWeek = getDayNameByDate(date);

    const overTime = getNightUserTotalOvertime(
      inTime,
      outTime,
      weeklySchedule[dayNameOfWeek]?.inTime,
      weeklySchedule[dayNameOfWeek]?.outTime,
      selectedUser?.overtimeAfterMinutes,
      rules,
      selectedDevice,
    );
    return overTime ? `${overTime} hrs` : "0.0";
  };

  const getLateTime = (inTime, outTime, date, calculateLateTime) => {
    if (!calculateLateTime) return "0.0";
    if (!inTime || !outTime) return "0.0";

    if (!selectedDevice) {
      return "";
    }

    if (!selectedUser?.weeklySchedule) {
      return "Weekly schedule not set";
    }

    let weeklySchedule = JSON.parse(selectedUser?.weeklySchedule || {});

    let rules = JSON.parse(selectedUser?.rules || "{}");

    const dayNameOfWeek = getDayNameByDate(date);

    const lateTime = getNightUserTotalLateTime(
      inTime,
      outTime,
      weeklySchedule[dayNameOfWeek]?.inTime,
      weeklySchedule[dayNameOfWeek]?.outTime,
      selectedUser?.relaxationMinutes,
      rules,
      selectedDevice,
    );
    return lateTime ? `${lateTime} hrs` : "0.0";
    // } else {
    //   return "0.0";
    // }
  };

  const totalHours = (inTime, outTime) => {
    if (!inTime || !outTime) return "0.0";

    if (!selectedDevice) {
      return "";
    }

    const totalHours = getNightUserTotalHours(inTime, outTime);
    return totalHours ? `${totalHours} hrs` : "0.0";
  };

  /*   All UseEffects
   ********************************************* */
  // Get logs when month selection changes
  useEffect(() => {
    let timeout = setTimeout(() => {
      let filterBy = searchParams.get("filterBy");
      if (selectedMonth && filterBy) {
        getLogsByMonth();
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [selectedMonth, searchParams]);

  useEffect(() => {
    if (selectedUser) {
      setUserData(selectedUser);
    }
  }, [selectedUser]);

  useEffect(() => {
    let timeout = setTimeout(() => {
      if (params.deviceId) {
        GetDeviceById(params.deviceId);
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [params]);

  return (
    <div className="px-2 py-2">
      <h3>Update Monthly Records</h3>
      {loading && <Spinner />}
      {/* User Navigation Controls */}
      {usersList.length > 1 && selectedUser && (
        <div className="d-flex align-items-center justify-content-between mb-3 p-2 bg-light rounded">
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={goToPrevUser}
            disabled={currentUserIndex <= 0}
          >
            <i className="bi bi-arrow-left"></i> Previous
          </button>

          <div className="text-center">
            <span className="fw-bold">{selectedUser.name || "User"}</span>
            <div className="small text-muted">
              {currentUserIndex + 1} of {usersList.length}
            </div>
          </div>

          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={goToNextUser}
            disabled={currentUserIndex >= usersList.length - 1}
          >
            Next <i className="bi bi-arrow-right"></i>
          </button>
        </div>
      )}
      <div className="mb-3">
        <label htmlFor="monthPicker" className="form-label">
          Select Month
        </label>
        <input
          type="month"
          className="form-control"
          id="monthPicker"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
        />
      </div>
      <div
        className="table-responsive "
        style={{
          height: "48vh",
          overflowY: "auto",
        }}
      >
        <table className="table table-striped table-bordered">
          <thead
            className="custom-shadow-sm"
            style={{
              // Sticky
              position: "sticky",
              top: 0,
              background: "#fff",
              zIndex: 1,
            }}
          >
            <tr>
              <th>Date</th>
              <th>Check-In</th>
              <th>Check-Out</th>
              <th>Total Hours</th>
              <th>Late Time</th>
              <th>Calculate Overtime</th>
            </tr>
          </thead>
          <tbody>
            {selectedDevice &&
              monthlyRecords?.map((record, index) => (
                <tr key={record.date}>
                  <td
                    className={`py-1 ${record.isHoliday ? "text-success" : ""}`}
                  >
                    {`  ${record.date} - 
                    ${getDayNameByDate(record.date, 3)}`}
                  </td>
                  <td className="py-1">
                    <input
                      disabled={loading}
                      type="time"
                      className="form-control"
                      value={record.inTime || ""}
                      onChange={(e) =>
                        handleRecordChange(index, "inTime", e.target.value)
                      }
                    />
                  </td>
                  <td className="py-1">
                    <input
                      disabled={loading}
                      type="time"
                      className="form-control"
                      value={record.outTime || ""}
                      onChange={(e) =>
                        handleRecordChange(index, "outTime", e.target.value)
                      }
                    />
                  </td>
                  <td className="py-1">
                    <label htmlFor="lateTime">
                      {totalHours(record.inTime, record.outTime)}
                    </label>
                  </td>
                  <td className="py-1">
                    <div className="form-check">
                      <input
                        disabled={loading}
                        type="checkbox"
                        className="form-check-input"
                        checked={record.calculateLateTime}
                        onChange={(e) =>
                          handleCheckBoxChange(
                            "calculateLateTime",
                            e.target.checked,
                            record.date,
                          )
                        }
                      />
                      <label className="form-check-label">
                        Calculate ({" "}
                        {getLateTime(
                          record.inTime,
                          record.outTime,
                          record.date,
                          record.calculateLateTime,
                        )}
                        )
                      </label>
                    </div>
                  </td>
                  <td className="py-1">
                    <div className="form-check">
                      <input
                        disabled={loading}
                        type="checkbox"
                        className="form-check-input"
                        checked={record.calculateOverTime}
                        onChange={(e) =>
                          handleCheckBoxChange(
                            "calculateOverTime",
                            e.target.checked,
                            record.date,
                          )
                        }
                      />
                      <label className="form-check-label">
                        Calculate ({" "}
                        {getOvertime(
                          record.inTime,
                          record.outTime,
                          record.calculateOverTime,
                          record.date,
                          record.isHoliday,
                        )}{" "}
                        )
                      </label>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <div className="text-end mt-3">
        {selectedUser && selectedUser.deviceId && (
          <GenerateAttendance
            selectedMonth={selectedMonth}
            userData={selectedUser}
            deviceId={selectedUser.deviceId}
          />
        )}
        <button
          className="btn btn-primary ms-1"
          onClick={askSaveRecordsConfirmation}
          disabled={loading}
        >
          Save All Records
        </button>
      </div>
    </div>
  );
}

/**
 * Birds-eye View
 * *********************************************
 * Get Logs for the selected month
 *
 * --- Separate Logs
 * Divide Logs by 12:00 PM into inTime and outTime records
 * assign logs of each date to inTime records and outTime records
 * let only on inTime and one outTime record per date
 *
 * --- Assign In-time and Out-time to correct dates
 * For each date, assign inTime from that date's inTime record
 * assign outTime from next date's outTime record
 *
 *
 *
 *
 *
 * Notes:
 * Get Processed Logs In-Time and Out-Time Logic for night shift users
 * *********************************************
 * Get create blank blank records for all dates in month
 * get logs for a date
 * divide logs into inRecords and outRecords
 *  ↪ inRecords = logs with time > 12:00 PM
 *  ↪ outRecords = logs with time < 12:00 PM
 *
 *
 *
 */
